import type { PaxtonHighlightArea } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import type { ContentViewerSession } from '@/content-viewer/store/schemas'
import type { AppDispatch } from '@/store/store'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import { fetchAndStoreUserFiles } from '@/user_files/files/utils/fetch-and-store-user-files'
import type { MedSummaryReference } from '@/medical-billing-summaries/references/store/schemas'
import { buildMedSummaryViewerSession } from './build-med-summary-viewer-session'
import { MedSummarySourceProvider, type MedSummarySourceRef } from './schemas'

export type OpenMedSummarySourceViewerOptions = {
  legacyReference?: MedSummaryReference
  highlights?: PaxtonHighlightArea[]
  startingPage?: number
  // Asset surface, non-PDF source: open the stored conversion (AI-94) instead of the original
  convertedPdf?: { assetId: string; userFileId: string }
}

export type OpenMedSummarySourceViewerResult =
  | {
      opened: true
      session: ContentViewerSession
    }
  | {
      opened: false
      error: string
    }

type OpenMedSummarySourceViewerArgs = {
  source: MedSummarySourceRef
  options?: OpenMedSummarySourceViewerOptions
  loadedUserFilesById: Record<string, UserFileRead>
  dispatch: AppDispatch
}

export async function openMedSummarySourceViewer(args: OpenMedSummarySourceViewerArgs): Promise<OpenMedSummarySourceViewerResult> {
  const { source, options = {}, loadedUserFilesById, dispatch } = args

  if (source.provider === MedSummarySourceProvider.LEGACY) {
    const session = buildMedSummaryViewerSession({
      source,
      reference: options.legacyReference,
      highlights: options.highlights,
      startingPage: options.startingPage,
    })
    return session ? { opened: true, session } : { opened: false, error: 'Could not load this source file.' }
  }

  // Converted-PDF sessions resolve their bytes by (assetId, userFileId) at render time -
  // no user-file record needed (the title comes from the source ref).
  if (options.convertedPdf) {
    const session = buildMedSummaryViewerSession({
      source,
      reference: options.legacyReference,
      highlights: options.highlights,
      startingPage: options.startingPage,
      convertedPdf: options.convertedPdf,
    })
    return session ? { opened: true, session } : { opened: false, error: 'Could not load this source file.' }
  }

  let userFile = loadedUserFilesById[source.source_id]
  if (!userFile) {
    const records = await fetchAndStoreUserFiles([source.source_id], dispatch)
    userFile = records[source.source_id]
  }

  const session = buildMedSummaryViewerSession({
    source,
    reference: options.legacyReference,
    userFile,
    highlights: options.highlights,
    startingPage: options.startingPage,
  })

  return session ? { opened: true, session } : { opened: false, error: 'Could not load this source file.' }
}
